import { z } from "zod";

import { paginationQuerySchema, paginatedSchema } from "../common/pagination";
import { telemetryProviderSchema } from "../devices/devices";

/**
 * Statuses stored on `WebhookEvent`.
 *
 * Distinct from `UplinkResult` only in that the row can still be `RECEIVED`
 * or `FAILED` mid-flight; the ack returned to the Network Server collapses
 * those into outcomes the provider understands.
 */
export const WEBHOOK_EVENT_STATUSES = [
  "RECEIVED",
  "PROCESSED",
  "DUPLICATE",
  "FAILED",
  "DEAD_LETTER",
] as const;

export const webhookEventStatusSchema = z.enum(WEBHOOK_EVENT_STATUSES);
export type WebhookEventStatus = z.infer<typeof webhookEventStatusSchema>;

export const webhookEventSchema = z.object({
  id: z.uuid(),
  provider: telemetryProviderSchema,
  providerEventId: z.string().min(1),
  status: webhookEventStatusSchema,
  deviceId: z.uuid().nullable(),
  /** Present when the event matched a device the caller is allowed to see. */
  organizationId: z.uuid().nullable(),
  organizationName: z.string().nullable(),
  deviceName: z.string().nullable(),
  devEui: z.string().nullable(),
  receivedAt: z.iso.datetime(),
  processedAt: z.iso.datetime().nullable(),
  error: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  /**
   * Sanitized slice of the stored envelope: enough to diagnose a mapping
   * problem, never the full payload a Network Server might have stuffed with
   * identifiers we do not need to show.
   */
  summary: z.record(z.string(), z.unknown()).nullable(),
});

export type WebhookEvent = z.infer<typeof webhookEventSchema>;

export const listWebhookEventsQuerySchema = paginationQuerySchema.extend({
  status: webhookEventStatusSchema.optional().default("DEAD_LETTER"),
  search: z.string().trim().min(1).max(100).optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});

export type ListWebhookEventsQuery = z.infer<typeof listWebhookEventsQuerySchema>;

export const webhookEventListSchema = paginatedSchema(webhookEventSchema);
export type WebhookEventList = z.infer<typeof webhookEventListSchema>;
